/**
 * Category Service
 * Manages category operations, user custom categories, and Firestore integration
 */

import { db } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy 
} from 'firebase/firestore';
import { 
  EXPENSE_CATEGORIES, 
  INCOME_CATEGORIES, 
  SAVINGS_CATEGORIES,
  CATEGORY_TYPES,
  getCategoriesByType,
  getCategoryById
} from '@/constants/categoryTaxonomy';

const COLLECTIONS = {
  CUSTOM_CATEGORIES: 'customCategories',
  CATEGORY_PREFERENCES: 'categoryPreferences',
  CATEGORY_ANALYTICS: 'categoryAnalytics'
};

/**
 * Get all categories for user (default + custom)
 * @param {String} userId 
 * @param {String} type - 'expense', 'income', 'savings', or null for all
 * @returns {Array}
 */
export async function getAllCategories(userId, type = null) {
  // Get default categories
  let defaultCategories = [];
  if (!type) {
    defaultCategories = [
      ...EXPENSE_CATEGORIES,
      ...INCOME_CATEGORIES,
      ...SAVINGS_CATEGORIES
    ];
  } else {
    defaultCategories = getCategoriesByType(type);
  }

  // Get custom categories from Firestore
  const customCategories = await getCustomCategories(userId, type);

  // Merge and return
  return [
    ...defaultCategories.map(cat => ({ ...cat, isDefault: true })),
    ...customCategories.map(cat => ({ ...cat, isDefault: false }))
  ];
}

/**
 * Get the canonical category ID for any category (default or custom)
 * This is used to resolve custom categories to their linked canonical category for budget matching
 * @param {String} userId 
 * @param {String} categoryId 
 * @returns {String} Canonical category ID from categoryTaxonomy.js
 */
export async function getCanonicalCategoryId(userId, categoryId) {
  if (!categoryId) return 'miscellaneous';
  
  // Check if it's already a canonical category from categoryTaxonomy
  const taxonomyCategory = getCategoryById(categoryId);
  if (taxonomyCategory) {
    return categoryId; // It's already canonical
  }
  
  // Otherwise, look up the custom category
  try {
    const categoryRef = doc(db, 'users', userId, 'customCategories', categoryId);
    const categorySnap = await getDoc(categoryRef);
    
    if (categorySnap.exists()) {
      const customCat = categorySnap.data();
      // Return the linked canonical category, or miscellaneous as fallback
      return customCat.linkedCanonicalCategory || 'miscellaneous';
    }
  } catch (error) {
    console.error('Error getting canonical category:', error);
  }
  
  return 'miscellaneous'; // Fallback
}

/**
 * Get custom categories for user
 * @param {String} userId 
 * @param {String} type 
 * @returns {Array}
 */
export async function getCustomCategories(userId, type = null) {
  try {
    // Access nested collection: /users/{userId}/customCategories
    const categoriesRef = collection(db, 'users', userId, 'customCategories');
    let q = query(
      categoriesRef,
      orderBy('createdAt', 'desc')
    );

    if (type) {
      q = query(
        categoriesRef,
        where('type', '==', type),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching custom categories:', error);
    return [];
  }
}

/**
 * Create custom category
 * @param {String} userId 
 * @param {Object} categoryData 
 * @returns {Object}
 */
export async function createCustomCategory(userId, categoryData) {
  try {
    // Access nested collection: /users/{userId}/customCategories
    const categoriesRef = collection(db, 'users', userId, 'customCategories');
    
    const newCategory = {
      userId,
      name: categoryData.name,
      type: categoryData.type || CATEGORY_TYPES.EXPENSE,
      icon: categoryData.icon || 'Tag',
      color: categoryData.color || '#6B7280',
      description: categoryData.description || '',
      subcategories: categoryData.subcategories || [],
      // Link to canonical category from categoryTaxonomy.js for budget envelope matching
      // Required for expense categories to ensure transactions flow to correct envelope
      linkedCanonicalCategory: categoryData.linkedCanonicalCategory || null,
      metadata: categoryData.metadata || {},
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(categoriesRef, newCategory);
    
    return {
      id: docRef.id,
      ...newCategory
    };
  } catch (error) {
    console.error('Error creating custom category:', error);
    throw error;
  }
}

/**
 * Update custom category
 * @param {String} categoryId 
 * @param {Object} updates 
 * @returns {Boolean}
 */
export async function updateCustomCategory(userId, categoryId, updates) {
  try {
    // Access nested collection: /users/{userId}/customCategories/{categoryId}
    const categoryRef = doc(db, 'users', userId, 'customCategories', categoryId);
    
    await updateDoc(categoryRef, {
      ...updates,
      updatedAt: new Date()
    });

    return true;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
}

/**
 * Delete custom category
 * @param {String} userId
 * @param {String} categoryId 
 * @returns {Boolean}
 */
export async function deleteCustomCategory(userId, categoryId) {
  try {
    // Access nested collection: /users/{userId}/customCategories/{categoryId}
    const categoryRef = doc(db, 'users', userId, 'customCategories', categoryId);
    await deleteDoc(categoryRef);
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
}

/**
 * Get category preferences for user
 * @param {String} userId 
 * @returns {Object}
 */
export async function getCategoryPreferences(userId) {
  try {
    // Access nested collection: /users/{userId}/categoryPreferences/{userId}
    const prefRef = doc(db, 'users', userId, 'categoryPreferences', userId);
    const prefDoc = await getDoc(prefRef);

    if (prefDoc.exists()) {
      return prefDoc.data();
    }

    // Return default preferences
    return {
      userId,
      favoriteCategories: [],
      hiddenCategories: [],
      categoryOrder: [],
      defaultBudgets: {},
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error fetching preferences:', error);
    return {
      favoriteCategories: [],
      hiddenCategories: [],
      categoryOrder: [],
      defaultBudgets: {}
    };
  }
}

/**
 * Update category preferences
 * @param {String} userId 
 * @param {Object} preferences 
 * @returns {Boolean}
 */
export async function updateCategoryPreferences(userId, preferences) {
  try {
    // Access nested collection: /users/{userId}/categoryPreferences/{userId}
    const prefRef = doc(db, 'users', userId, 'categoryPreferences', userId);
    const prefDoc = await getDoc(prefRef);

    const data = {
      ...preferences,
      userId,
      updatedAt: new Date()
    };

    if (prefDoc.exists()) {
      await updateDoc(prefRef, data);
    } else {
      await setDoc(prefRef, {
        ...data,
        createdAt: new Date()
      });
    }

    return true;
  } catch (error) {
    console.error('Error updating preferences:', error);
    throw error;
  }
}

/**
 * Add category to favorites
 * @param {String} userId 
 * @param {String} categoryId 
 * @returns {Boolean}
 */
export async function addToFavorites(userId, categoryId) {
  try {
    const preferences = await getCategoryPreferences(userId);
    const favorites = new Set(preferences.favoriteCategories || []);
    favorites.add(categoryId);

    await updateCategoryPreferences(userId, {
      favoriteCategories: Array.from(favorites)
    });

    return true;
  } catch (error) {
    console.error('Error adding to favorites:', error);
    throw error;
  }
}

/**
 * Remove category from favorites
 * @param {String} userId 
 * @param {String} categoryId 
 * @returns {Boolean}
 */
export async function removeFromFavorites(userId, categoryId) {
  try {
    const preferences = await getCategoryPreferences(userId);
    const favorites = new Set(preferences.favoriteCategories || []);
    favorites.delete(categoryId);

    await updateCategoryPreferences(userId, {
      favoriteCategories: Array.from(favorites)
    });

    return true;
  } catch (error) {
    console.error('Error removing from favorites:', error);
    throw error;
  }
}

/**
 * Get category analytics
 * @param {String} userId 
 * @param {String} categoryId 
 * @param {Object} dateRange 
 * @returns {Object}
 */
export async function getCategoryAnalytics(userId, categoryId, dateRange = null) {
  try {
    // This would query the transactions collection
    // For now, return structure
    return {
      categoryId,
      totalAmount: 0,
      transactionCount: 0,
      avgTransaction: 0,
      trend: 'stable', // 'increasing', 'decreasing', 'stable'
      comparedToLastPeriod: {
        amount: 0,
        percentage: 0
      },
      topSubcategories: [],
      recentTransactions: []
    };
  } catch (error) {
    console.error('Error fetching category analytics:', error);
    return null;
  }
}

/**
 * Get frequently used categories
 * @param {String} userId 
 * @param {Number} limit 
 * @returns {Array}
 */
export async function getFrequentlyUsedCategories(userId, limit = 5) {
  // TODO: Query transactions to find most used categories
  // This will be implemented when transaction service is ready
  return [];
}

/**
 * Get recently used categories
 * @param {String} userId 
 * @param {Number} limit 
 * @returns {Array}
 */
export async function getRecentlyUsedCategories(userId, limit = 5) {
  // TODO: Query recent transactions to find categories
  // This will be implemented when transaction service is ready
  return [];
}

/**
 * Suggest category based on description
 * @param {String} description 
 * @param {String} amount 
 * @returns {Object}
 */
export function suggestCategory(description, amount = null) {
  const desc = description.toLowerCase();

  // Grocery keywords
  if (desc.includes('grocery') || desc.includes('food') || desc.includes('market')) {
    return getCategoryById('groceries-food', CATEGORY_TYPES.EXPENSE);
  }

  // Utilities keywords
  if (desc.includes('electric') || desc.includes('water') || desc.includes('gas') || desc.includes('internet')) {
    return getCategoryById('utilities-bills', CATEGORY_TYPES.EXPENSE);
  }

  // Housing keywords
  if (desc.includes('rent') || desc.includes('mortgage') || desc.includes('hoa')) {
    return getCategoryById('housing-rent', CATEGORY_TYPES.EXPENSE);
  }

  // Transportation keywords
  if (desc.includes('gas') || desc.includes('fuel') || desc.includes('uber') || desc.includes('lyft') || desc.includes('parking')) {
    return getCategoryById('transportation-auto', CATEGORY_TYPES.EXPENSE);
  }

  // Entertainment keywords
  if (desc.includes('movie') || desc.includes('netflix') || desc.includes('spotify') || desc.includes('concert')) {
    return getCategoryById('entertainment-leisure', CATEGORY_TYPES.EXPENSE);
  }

  // Restaurant keywords
  if (desc.includes('restaurant') || desc.includes('coffee') || desc.includes('cafe') || desc.includes('dining')) {
    return getCategoryById('groceries-food', CATEGORY_TYPES.EXPENSE);
  }

  // Health keywords
  if (desc.includes('doctor') || desc.includes('pharmacy') || desc.includes('medical') || desc.includes('hospital')) {
    return getCategoryById('health-medical', CATEGORY_TYPES.EXPENSE);
  }

  // Salary keywords (Income)
  if (desc.includes('salary') || desc.includes('paycheck') || desc.includes('wages')) {
    return getCategoryById('salary-wages', CATEGORY_TYPES.INCOME);
  }

  // Default to miscellaneous
  return getCategoryById('miscellaneous-other', CATEGORY_TYPES.EXPENSE);
}

/**
 * Validate category data
 * @param {Object} categoryData 
 * @returns {Object} { isValid: Boolean, errors: Array }
 */
export function validateCategoryData(categoryData) {
  const errors = [];

  if (!categoryData.name || categoryData.name.trim() === '') {
    errors.push('Category name is required');
  }

  if (categoryData.name && categoryData.name.length > 50) {
    errors.push('Category name must be less than 50 characters');
  }

  if (!categoryData.type || !Object.values(CATEGORY_TYPES).includes(categoryData.type)) {
    errors.push('Valid category type is required (expense, income, or savings)');
  }

  if (categoryData.subcategories && !Array.isArray(categoryData.subcategories)) {
    errors.push('Subcategories must be an array');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

export default {
  getAllCategories,
  getCustomCategories,
  getCanonicalCategoryId,
  createCustomCategory,
  updateCustomCategory,
  deleteCustomCategory,
  getCategoryPreferences,
  updateCategoryPreferences,
  addToFavorites,
  removeFromFavorites,
  getCategoryAnalytics,
  getFrequentlyUsedCategories,
  getRecentlyUsedCategories,
  suggestCategory,
  validateCategoryData
};
